/**
 * Models for the Checkout page — mirrors the backend Smithy model
 * defined in RnRRCCFacadeServiceModel.
 */

/** Process path function type — DIRECT or INDIRECT */
export enum ProcessPathFunctionType {
  DIRECT = 'DIRECT',
  INDIRECT = 'INDIRECT',
}

/** Per-process-path detail in the DryRunShiftPlan response (1:1 with backend) */
export interface DryRunProcessPathEntry {
  /** Human-readable function name */
  functionName: string;
  /** FCLM function identifier for the process path */
  functionId?: string;
  /** Type of the process path function */
  type: ProcessPathFunctionType;
  /** Maximum staffing constraint for this process path */
  maxStaffing?: number;
  /** Headcount allocated to this process path */
  headcount: number;
  /** Total hours for this process path */
  hours?: number;
  /** Hours per 1000 units (productivity metric) */
  hoursPer1000Units?: number;
}

/** Response from the DryRunShiftPlan API (1:1 with backend) */
export interface DryRunShiftPlanResponse {
  /** Plan ID echoed back for correlation */
  planId?: string;
  /** Total available headcount for the shift */
  availableHeadcount: number;
  /** Tuned headcount after output adjustments (equals availableHeadcount when no adjustments) */
  tunedHeadcount?: number;
  /** Total hours for the shift */
  totalHours: number;
  /** Per-process-path breakdown */
  processPath: DryRunProcessPathEntry[];
}

/** Request for the DryRunShiftPlan API */
export interface DryRunShiftPlanRequest {
  /** Unique identifier of the shift plan */
  planId: string;
  /** Warehouse code (e.g., LAS2) */
  warehouseId: string;
  /** Operational fulfillment date in YYYY-MM-DD format */
  ofdDate: string;
  /** Clock window name (e.g., DAY, NIGHT) identifying the shift */
  clockWindow: string;
  /** Input overrides from the Volume page (JSON string) */
  inputOverrides: string;
  /** Per-process-path output adjustments from sliders (null on initial load) */
  outputAdjustment?: OutputAdjustmentEntry[] | null;
}

/** A single per-process-path output adjustment entry from the Checkout page */
export interface OutputAdjustmentEntry {
  /** FCLM function identifier for the process path being adjusted */
  functionId?: string;
  /** Human-readable function name (e.g., Primary Grading) */
  functionName: string;
  /** Percentage on 0-200 scale (100 = no change, 90 = -10%, 115 = +15%) */
  overridePercentage: number;
  /** Reason for the adjustment provided by the operator */
  overrideReason?: string;
  /** Whether this process path is disabled (excluded from plan output) */
  disabled?: boolean;
}

// ─── Component Props ────────────────────────────────────────────────────────────

/** Override reason options for the Checkout page dropdown */
export const OVERRIDE_REASON_OPTIONS = [
  'Volume forecast adjustment',
  'Associate availability constraint',
  'Training / onboarding',
  'Equipment downtime',
  'Process path shutdown (planned)',
  'Flex-up for demand spike',
  'Labor share / cross-trained transfer',
  'Manager discretion',
  'Safety constraint',
  'Quality improvement focus',
  'Other',
] as const;

export type OverrideReasonOption = typeof OVERRIDE_REASON_OPTIONS[number];

/** Props for the TuningSlider component */
export interface TuningSliderProps {
  /** Current slider value (0–200, where 100 = no change) */
  value: number;
  /** Whether the slider is disabled (e.g., path excluded) */
  disabled: boolean;
  /** Maximum slider value based on max staffing constraint */
  maxSliderValue?: number;
  /** Callback when the slider value changes */
  onChange: (value: number) => void;
}

/** Props for the SummaryStatsRow component */
export interface SummaryStatsRowProps {
  /** Total available headcount from the API */
  availableHeadcount: number;
  /** Sum of tuned HC across all included paths */
  tunedHeadcount: number;
  /** Total hours for the shift */
  totalHours: number;
  /** Direct function hours */
  directHours: number;
  /** Indirect function hours */
  indirectHours: number;
  /** Number of included (non-excluded) process paths */
  includedPathCount: number;
  /** Number of excluded process paths */
  excludedPathCount: number;
}
